'use client';

import { getConfig } from '@sortlist-frontend/config';
import { Button } from '@sortlist-frontend/design-system';
import Image from 'next/image';

import { linksConfigFactory } from '_config/site.links.config';

const { publicEnv } = getConfig();
const { s3Bucket: s3BucketUrl } = publicEnv;

const facebookIcon = `${s3BucketUrl}/icons/social/facebook.svg`;
const instagramIcon = `${s3BucketUrl}/icons/social/instagram.svg`;
const linkedinIcon = `${s3BucketUrl}/icons/social/linkedin.svg`;
const xIcon = `${s3BucketUrl}/icons/social/x.svg`;
const youtubeIcon = `${s3BucketUrl}/icons/social/youtube.svg`;

// The icons are tinted with an SVG feFlood/feComposite filter rather than a CSS `hue-rotate()` recolor
// chain: that chain is only an approximation and each engine rounds it differently, which rendered
// these purple in Safari. feFlood pinned to sRGB carries no colour maths, so the tint is exact
// everywhere. It sits on a wrapper rather than the image because Safari is unreliable filtering
// replaced elements.
const SOCIAL_TINT_FILTER_ID = 'footer-social-tint';
const SOCIAL_TINT_COLOR = '#305EF3'; // primary-600

const { social: socialLinks } = linksConfigFactory({});

export const Socials = () => {
  const socials = [
    { alt: 'x', href: socialLinks.x, src: xIcon },
    { alt: 'Facebook', href: socialLinks.facebook, src: facebookIcon },
    { alt: 'Linkedin', href: socialLinks.linkedin, src: linkedinIcon },
    { alt: 'Instagram', href: socialLinks.instagram, src: instagramIcon },
    { alt: 'YouTube', href: socialLinks.youtube, src: youtubeIcon },
  ];

  // Open on press so the real URL stays out of the DOM (no crawlable href), replacing the previous
  // Obfuscate wrapper while keeping the icon buttons as design-system components.
  const openSocial = (href: string) => window.open(href, '_blank', 'noopener,noreferrer');

  return (
    <div className="my-8 flex flex-row gap-2">
      <svg aria-hidden="true" focusable="false" width="0" height="0" className="absolute">
        <filter id={SOCIAL_TINT_FILTER_ID} colorInterpolationFilters="sRGB">
          <feFlood floodColor={SOCIAL_TINT_COLOR} result="tint" />
          <feComposite in="tint" in2="SourceAlpha" operator="in" />
        </filter>
      </svg>

      {socials.map((social) => (
        <Button
          key={social.alt}
          isIconOnly
          variant="flat"
          color="primary"
          radius="full"
          size="sm"
          aria-label={social.alt}
          onPress={() => openSocial(social.href)}>
          <span className="inline-flex [filter:url(#footer-social-tint)]">
            <Image src={social.src} alt={social.alt} width={14} height={14} />
          </span>
        </Button>
      ))}
    </div>
  );
};
